import React, { useState } from "react";
import { t } from "i18next";
import { colors, css, fonts, frames } from "../../Styles";
import { LineSegment, VictoryAxis, VictoryBar, VictoryChart, VictoryGroup, VictoryLabel, VictoryTooltip } from "victory";
import { Animal } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";
import { useAnimalConsumptionByMultipleIds } from "../../Services/Agrisys_GqlDataCaller/ConsumptionCalls/consumptionSlice";

const _feedCurveWidth = 600;
const _chartWidth = 550;
const _chartHeight = 300;
const _fontSize = { ...fonts.fontSize4 };
const _primaryColor = colors.greyPrimary;

interface DayData {
  dayOffset: number;
  total: number;
  progressed: number;
  date: Date;
}

interface TenDaysGraph_Props {
  data: Animal[];
}

export default function TenDaysGraph({ data }: TenDaysGraph_Props) {
  const [hoveredLabel, setHoveredLabel] = useState<number | null>(null);
  const today = new Date();
  const grouped = new Map<number, { total: number; progressed: number }>();
  
  // #region | Data
  const consumptionEntities = useAnimalConsumptionByMultipleIds(data.map(a => a.animal_id.toString()))
  const consumptionsArray = Object.values(consumptionEntities).filter((entity) => !entity.isLoading && entity.entity !== undefined).map((entityConsumption) => entityConsumption.entity?.consumptions)

  consumptionsArray.filter(Boolean).flat().forEach(consumption => {
    const offset = Math.abs(consumption?.milestoneDayOffset ?? 0);

    if (!grouped.has(offset)) { grouped.set(offset, { total: 0, progressed: 0 }) }
    const entry = grouped.get(offset)!;

    entry.total += consumption?.total ?? 0;
    entry.progressed += consumption?.progressed ?? 0;
  });

  const last10Days: DayData[] = Array.from({ length: 10 }, (_, i) => {
    const offset = 9 - i;
    const data = grouped.get(offset) ?? { total: 0, progressed: 0 };
  
    return {
      dayOffset: offset,
      total: data.total,
      progressed: data.progressed,
      date: new Date(today.getFullYear(), today.getMonth(), today.getDate() - offset),
    };
  });
  // #endregion

  // #region | Graph Width
  // To achieve this, I had to reset svg overflow to "visible" in reset.css // MWE
  const maxValue = Math.max(...last10Days.map((d) => d.total ?? 0));
  const feedTotalRange = [0, maxValue / 1000];

  const longestNumber = feedTotalRange.reduce((maxNumber, currentNumber) => {
    return Math.round(currentNumber) > Math.round(maxNumber)
      ? Math.round(currentNumber)
      : Math.round(maxNumber);
  }, Math.round(feedTotalRange[0]));

  const longestNumberLenght = Number(longestNumber.toString().replace(/[.,]/g, '').length)

  const _paddingLeft = longestNumberLenght <= 2 ? 0 : ((longestNumberLenght - 2) * 8);
  // #endregion

  // #region | Tooltip
  const tooltip = (
    <VictoryTooltip
      dy={-3}
      cornerRadius={8}
      pointerLength={6}
      pointerWidth={12}
      flyoutPadding={{ top: 1, bottom: 1, left: 6, right: 6 }}
      flyoutStyle={{
        fill: colors.greyDarkPrimary,
        stroke: 0
      }}
      style={{
        fill: colors.greyLightTertiary,
        fontSize: "11px",
        ...fonts.paragraph,
      }}
    />
  )
  // #endregion

  // #region | Bars
  const feedTotalBars = (
    <VictoryBar
      data={last10Days}
      x={(d) => String(d.dayOffset)}
      y={(d) => d.total / 1000}
      style={{
        data: {
          width: 10,
          fill: hoveredLabel === 3 ? colors.greyTertiary : colors.graphBlueFour,
          transition: "0.3s",
        },
      }}
      labels={({ datum }) => `${t("feed.general.expected", { defaultValue: "Expected" })}: ${(datum.total/1000).toFixed(2)} kg`}
      labelComponent={tooltip}
    />
  );

  const feedConsumptionBars = (
    <VictoryBar
      data={last10Days}
      x={(d) => String(d.dayOffset)}
      y={(d) => d.progressed / 1000}
      style={{
        data: {
          width: 10,
          fill: hoveredLabel === 2 ? colors.greyTertiary : colors.graphBlueOne,
          transition: "0.3s",
        },
      }}
      labels={({ datum }) => `${t("feed.general.actually", { defaultValue: "Consumed" })}: ${(datum.progressed / 1000).toFixed(2)} kg`}
      labelComponent={tooltip}
    />
  );
  // #endregion

  // #region | Axes
  // Y-Axis
  const yAxis = (
    <VictoryAxis
      offsetX={32}
      dependentAxis
      standalone={false}
      style={{
        ticks: { stroke: _primaryColor, size: 5 },
        axis: { stroke: _primaryColor, strokeWidth: 2 },
      }}
      crossAxis={false}
      label={t("units.kg", { defaultValue: "KG" })}
      tickLabelComponent={
        <VictoryLabel
          textAnchor="end"
          style={{ ...fonts.paragraph, fill: _primaryColor, ..._fontSize }}
        />
      }
      axisLabelComponent={
        <VictoryLabel
          angle={0}
          verticalAnchor="start" 
          textAnchor="start"
          x={0}
          y={0}
          style={{ ...fonts.header, fill: _primaryColor, ..._fontSize }}
        />
      }
      axisComponent={
        <LineSegment
          y1={0}
          y2={_chartHeight - 20}
        />
      }
    />
  )

  // X-Axis
  const xAxis = (
    <VictoryAxis
      offsetY={25}
      orientation="bottom"
      standalone={false}
      tickCount={10}
      style={{
        ticks: { stroke: _primaryColor, size: 5 },
        axis: { stroke: _primaryColor, strokeWidth: 2 },
      }}
      tickValues={last10Days.map(d => String(d.dayOffset))}
      tickFormat={(t: string) => {
        const offset = Number(t);
        const date = new Date(today);
        date.setDate(today.getDate() - offset);
        return `${date.getDate()}/${date.getMonth() + 1}`;
      }}
      
      crossAxis={false}
      label={t("units.date", {defaultValue: "Date"})}
      tickLabelComponent={
        <VictoryLabel
          y={_chartHeight}
          verticalAnchor="end"
          style={{ ...fonts.paragraph, fill: _primaryColor, ..._fontSize }}
        />
      }
      axisLabelComponent={
        <VictoryLabel
          y={_chartHeight}
          x={_chartWidth}
          verticalAnchor="end"
          textAnchor="end"
          style={{ ...fonts.header, fill: _primaryColor, ..._fontSize }}
        />
      }
      axisComponent={ <LineSegment x1={27} x2={_chartWidth} /> }
    />
  )
  // #endregion

  return (
    consumptionsArray.length > 0 ?
    <div style={{ width: _feedCurveWidth, display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", paddingLeft: _paddingLeft}}>
      
      <VictoryChart
        padding={{ top: 40, bottom: 25, left: 45, right: 40 }}
        width={_chartWidth}
        height={_chartHeight}
        // style={{ parent: { pointerEvents: "auto", userSelect: "auto", touchAction: "auto" } }}
      >
        <VictoryGroup offset={12}>
          {feedTotalBars}
          {feedConsumptionBars}
        </VictoryGroup>
        {yAxis}
        {xAxis}
      </VictoryChart>
            
      <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
        <frames.ComponentLabel
          $component={3}
          $isHovered={hoveredLabel === 3}
          onMouseEnter={() => setHoveredLabel(3)}
          onMouseLeave={() => setHoveredLabel(null)}
        >
          {t("feed.general.expected", { defaultValue: "expected consumption" })}
        </frames.ComponentLabel>
        <frames.ComponentLabel
          $component={2}
          $isHovered={hoveredLabel === 2}
          onMouseEnter={() => setHoveredLabel(2)}
          onMouseLeave={() => setHoveredLabel(null)}
        >
          {t("feed.general.actually", {defaultValue: "actually consumed"})}
        </frames.ComponentLabel>
      </div>
      
    </div> : <p style={{...css.emptyPen, opacity: "0.5" }}>{t("noData", {defaultValue: "no data"})}</p>
  );
}